/**
 * CoverageView — THE single data-bearing coverage implementation
 * (docs/COVERAGE_ROUTE_CONTRACT.md: one implementation, two routes).
 *
 * Receives VALIDATED context as props and only then fetches coverage.
 * It never selects context, never defaults, and renders no totals or
 * percentages before a successful coverage response.
 *
 * Phase 4b: the API is the single source of element truth. Every coverage row
 * carries elements[] (typed: quantitative | metadata | narrative | decision |
 * calculation) with a server-DERIVED state from the status dimensions, plus a
 * server-computed `defended` flag — the ONLY thing allowed to light the
 * Defended badge. This view renders; it never re-derives compliance.
 *
 * Honesty contract (unchanged):
 * - "Confirmed" = a reviewer accepted this evidence mapping. Never an
 *   org-level compliance statement.
 * - "Defended" = every applicable element complete + confirmed + current,
 *   as computed by the backend truth table.
 * - Status is never conveyed by color alone (icon + label; auditors print
 *   grayscale).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Shield, Clock3, AlertTriangle, CircleDashed, ChevronDown,
  ChevronRight, UploadCloud, Calculator, UserCheck, ExternalLink, RefreshCw,
  Undo2, X, FileText, ClipboardList, EyeOff,
} from "lucide-react";
import {
  C, MONO, SANS, apiFetch, fmt, Data, Badge, Card, Overline, Btn,
  EmptyState, Skeleton, ErrorBanner, th, td,
} from "../../components/esg/EsgKit";

// Visual states arrive DERIVED from the backend; this map only styles them.
const STATE = {
  emerald: { c: C.primary,     Icon: ShieldCheck,  label: "Confirmed" },
  amber:   { c: C.accent,      Icon: Clock3,       label: "Awaiting review" },
  red:     { c: C.destructive, Icon: AlertTriangle, label: "Blocked" },
  muted:   { c: C.muted,       Icon: CircleDashed, label: "No evidence yet" },
};
const TYPE_ICON = {
  quantitative: null, calculation: Calculator,
  metadata: ClipboardList, narrative: FileText, decision: UserCheck,
};
const REASON_TEXT = {
  wrong_granularity: "Needs the owning module's calculation — corporate totals don't apply.",
  standard_superseded: "Standard version not effective for this cycle — map to the successor standard.",
  methodology_incompatible: "Unit or methodology incompatible with this element.",
  not_applicable: "Recorded omission decision.",
};
const REJECT_REASONS = [
  "methodology_incompatible", "wrong_granularity", "period_mismatch",
  "boundary_mismatch", "missing_disaggregation",
];
const OMITTED = (a) => a === "not_material" || a === "not_applicable";
const short = (s) => (s.length > 34 ? s.slice(0, 33) + "…" : s);

// ─── Summary over API elements (applicable = the auditable denominator) ───────
// State-driven, not disclosure-driven: an attested metadata/narrative element
// is SUPPORTED (emerald via its review dimension) even though no disclosure
// row exists for it. Blocked (red-gated) elements count as missing in the
// header; their reason renders on the row.
function summarize(coverage) {
  let supported = 0, confirmed = 0, awaiting = 0, rejected = 0, missing = 0,
      omitted = 0;
  for (const row of coverage) {
    for (const el of row.elements || []) {
      if (OMITTED(el.applicability)) { omitted += 1; continue; }
      if (el.review === "rejected") { rejected += 1; continue; }
      if (el.state === "emerald") { supported += 1; confirmed += 1; }
      else if (el.state === "amber") { supported += 1; awaiting += 1; }
      else { missing += 1; }
    }
  }
  return { supported, confirmed, awaiting, rejected, missing, omitted,
           total: supported + rejected + missing };
}

// ─── Element row ──────────────────────────────────────────────────────────────
function ElementRow({ el, highlight, onConfirm, onReject, onOmit, onAttest, busy }) {
  const [arm, setArm] = useState(null);       // "confirm" | "reject" | "omit" | "attest"
  const [reason, setReason] = useState(REJECT_REASONS[0]);
  const [statement, setStatement] = useState("");
  const st = STATE[el.state] || STATE.muted;
  const TIcon = TYPE_ICON[el.element_type];
  const dim = highlight && el.metric_code !== highlight;
  const isOmitted = OMITTED(el.applicability);
  return (
    <tr style={{ opacity: dim ? 0.35 : 1, transition: "opacity 180ms ease-out" }}
        data-testid={`element-${el.element_id}`}>
      <td style={{ ...td, paddingLeft: 34 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          {TIcon && <TIcon size={12} strokeWidth={1.5} color={C.muted} />}
          <span>
            <Data size={11} color={C.body}>
              {el.element_id.split(".").slice(1).join(".")}
            </Data>
            <span style={{ display: "block", fontFamily: SANS, fontSize: 10,
                           color: C.muted, maxWidth: "44ch" }}>{el.label}</span>
          </span>
        </span>
      </td>
      <td style={td}>
        {el.value != null
          ? <><Data size={11}>{fmt(el.value)} </Data>
              <Data size={10} color={C.muted} weight={500}>{el.unit}</Data></>
          : <span style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}>
              {el.element_type === "quantitative" ? "—" : el.element_type}
            </span>}
      </td>
      <td style={td}>
        {isOmitted
          ? <Badge c={C.muted} Icon={EyeOff}
                   label={el.applicability === "not_material" ? "Not material" : "Not applicable"} />
          : <Badge c={st.c} Icon={st.Icon} label={st.label} />}
        {el.reason_code && !isOmitted && (
          <span style={{ display: "block", fontFamily: SANS, fontSize: 10,
                         color: C.muted, marginTop: 3, maxWidth: "40ch" }}>
            {REASON_TEXT[el.reason_code] || el.reason_code}
          </span>
        )}
      </td>
      <td style={{ ...td, textAlign: "right", minWidth: 220 }}>
        {!arm && !isOmitted && el.state !== "red" && (
          <span style={{ display: "inline-flex", gap: 6 }}>
            {el.disclosure_id && el.state === "amber" && (
              <>
                <Btn variant="ghost" Icon={UserCheck} disabled={busy}
                     title="Records reviewer acceptance of this evidence mapping"
                     onClick={() => setArm("confirm")}>Confirm</Btn>
                <Btn variant="ghost" Icon={X} disabled={busy}
                     title="Reject with a structured reason"
                     onClick={() => setArm("reject")}>Reject</Btn>
              </>
            )}
            {el.state === "emerald" && el.disclosure_id && (
              <Btn variant="ghost" Icon={Undo2} disabled={busy}
                   title="Undo confirmation (audited)"
                   onClick={() => onConfirm(el, "unconfirm")}>Unconfirm</Btn>
            )}
            {!el.disclosure_id && el.element_type !== "calculation"
              && el.element_type !== "quantitative" && el.state !== "emerald" && (
              <Btn variant="ghost" Icon={UserCheck} disabled={busy}
                   title="Reviewer attests this element is addressed (audited)"
                   onClick={() => setArm("attest")}>Attest…</Btn>
            )}
            {!el.disclosure_id && el.state === "emerald" && (
              <Btn variant="ghost" Icon={Undo2} disabled={busy}
                   title="Revert the attestation (audited)"
                   onClick={() => onAttest(el, "none", "")}>Revert</Btn>
            )}
            {!el.disclosure_id && el.element_type !== "calculation" && (
              <Btn variant="ghost" Icon={EyeOff} disabled={busy}
                   title="Record a reasoned omission (requires a statement)"
                   onClick={() => setArm("omit")}>Omit…</Btn>
            )}
          </span>
        )}
        {arm === "attest" && (
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input value={statement} onChange={(e) => setStatement(e.target.value)}
                   placeholder="Where is this addressed? (optional)"
                   aria-label="Attestation comment"
                   style={{ padding: "5px 8px", borderRadius: 6, background: C.surface,
                            border: `1px solid ${C.border}`, color: C.ink,
                            fontFamily: SANS, fontSize: 11, width: 220 }} />
            <Btn variant="primary" disabled={busy}
                 onClick={() => { onAttest(el, "confirmed", statement); setArm(null); setStatement(""); }}>
              Attest
            </Btn>
            <Btn variant="ghost" disabled={busy} onClick={() => setArm(null)}>Back</Btn>
          </span>
        )}
        {arm === "confirm" && (
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontFamily: SANS, fontSize: 11, color: C.body }}>
              Accept this mapping?
            </span>
            <Btn variant="primary" disabled={busy}
                 onClick={() => { onConfirm(el, "confirm"); setArm(null); }}>Yes</Btn>
            <Btn variant="ghost" disabled={busy} onClick={() => setArm(null)}>Back</Btn>
          </span>
        )}
        {arm === "reject" && (
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
                    aria-label="Rejection reason" data-testid="reject-reason"
                    style={{ padding: "5px 8px", borderRadius: 6, background: C.surface,
                             border: `1px solid ${C.border}`, color: C.ink,
                             fontFamily: MONO, fontSize: 11 }}>
              {REJECT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <Btn variant="destructive" disabled={busy}
                 onClick={() => { onReject(el, reason); setArm(null); }}>Reject</Btn>
            <Btn variant="ghost" disabled={busy} onClick={() => setArm(null)}>Back</Btn>
          </span>
        )}
        {arm === "omit" && (
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input value={statement} onChange={(e) => setStatement(e.target.value)}
                   placeholder="Omission statement (required)"
                   aria-label="Omission statement"
                   style={{ padding: "5px 8px", borderRadius: 6, background: C.surface,
                            border: `1px solid ${C.border}`, color: C.ink,
                            fontFamily: SANS, fontSize: 11, width: 220 }} />
            <Btn variant="secondary" disabled={busy || !statement.trim()}
                 onClick={() => { onOmit(el, "not_material", statement); setArm(null); setStatement(""); }}>
              Not material
            </Btn>
            <Btn variant="ghost" disabled={busy} onClick={() => setArm(null)}>Back</Btn>
          </span>
        )}
      </td>
    </tr>
  );
}

// ─── Requirement row ──────────────────────────────────────────────────────────
function RequirementRow({ row, standard, highlight, expanded, onToggle,
                          onConfirm, onReject, onOmit, onAttest, busy }) {
  const els = row.elements || [];
  // State-driven, matching summarize(): an attested manual element (emerald,
  // no disclosure row) is supported — review Important #2.
  const supported = els.filter((e) => !OMITTED(e.applicability)
    && (e.state === "emerald" || e.state === "amber"));
  const involved = highlight
    ? supported.some((e) => e.metric_code === highlight)
    : false;
  const anyRed = els.some((e) => e.state === "red" && !OMITTED(e.applicability));
  const headState = row.defended ? STATE.emerald
    : supported.length ? STATE.amber
    : anyRed ? STATE.red : STATE.muted;
  const dim = highlight && !involved && !anyRed;
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); }
  };
  return (
    <>
      <tr onClick={onToggle} onKeyDown={onKey} tabIndex={0} role="button"
          aria-expanded={expanded} data-testid={`req-${row.requirement_id}`}
          style={{ cursor: "pointer", opacity: dim ? 0.35 : 1,
                   background: involved ? `${headState.c}0D` : "transparent",
                   transition: "opacity 180ms ease-out, background 180ms ease-out" }}>
        <td style={{ ...td, width: 26 }}>
          {expanded
            ? <ChevronDown size={14} strokeWidth={1.5} color={C.muted} />
            : <ChevronRight size={14} strokeWidth={1.5} color={C.muted} />}
        </td>
        <td style={td}>
          <Data size={12}>{row.requirement_id}</Data>
          <span style={{ display: "block", fontFamily: MONO, fontSize: 9,
                         color: C.muted, marginTop: 2 }}>{standard}</span>
        </td>
        <td style={td}>
          <span style={{ fontFamily: SANS, fontSize: 12, color: C.body }}>{row.framework}</span>
        </td>
        <td style={td} data-testid={`reqstate-${row.requirement_id}`}>
          {row.defended
            ? <Badge c={C.primary} Icon={Shield} label="Defended" />
            : <Badge c={headState.c} Icon={headState.Icon} label={headState.label} />}
        </td>
        <td style={td}>
          <Data size={11} color={supported.length ? C.ink : C.muted}>{supported.length}</Data>
          <span style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}> of </span>
          <Data size={11}>{els.filter((e) => !OMITTED(e.applicability)).length}</Data>
          <span style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}> elements</span>
        </td>
        <td style={{ ...td, textAlign: "right" }}>
          <Link to="/cross-framework-mapping"
                onClick={(e) => e.stopPropagation()}
                style={{ fontFamily: SANS, fontSize: 11, color: C.locked,
                         display: "inline-flex", alignItems: "center", gap: 4 }}>
            Framework reference <ExternalLink size={11} strokeWidth={1.5} />
          </Link>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: 0, borderBottom: `1px solid ${C.border}` }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {els.map((el) => (
                  <ElementRow key={el.element_id} el={el} highlight={highlight}
                              onConfirm={onConfirm} onReject={onReject}
                              onOmit={onOmit} onAttest={onAttest} busy={busy} />
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Next actions (typed, leverage-ranked) ────────────────────────────────────
function NextActions({ coverage, onFocusMetric }) {
  const actions = useMemo(() => {
    const out = [];
    const uploads = {}, resupply = {};
    let awaiting = 0;
    const calcs = [];
    for (const row of coverage) {
      for (const el of row.elements || []) {
        if (OMITTED(el.applicability)) continue;
        if (el.element_type === "calculation") continue;
        if (el.review === "rejected" && el.metric_code)
          (resupply[el.metric_code] = resupply[el.metric_code] || []).push(row);
        else if (!el.disclosure_id && el.metric_code)
          (uploads[el.metric_code] = uploads[el.metric_code] || []).push(row);
        else if (el.disclosure_id && el.state === "amber") awaiting += 1;
      }
      if (row.elements?.some((e) => e.element_type === "calculation"
                                    && e.state === "red"))
        calcs.push(row);
    }
    for (const [m, rows] of Object.entries(uploads))
      out.push({ Icon: UploadCloud, c: C.accent, key: `u-${m}`,
                 title: `Upload data: ${m}`,
                 detail: `Completes ${rows.length} element${rows.length > 1 ? "s" : ""} across ${new Set(rows.map((r) => r.framework)).size} framework${new Set(rows.map((r) => r.framework)).size > 1 ? "s" : ""}`,
                 leverage: rows.length, onClick: () => onFocusMetric(m) });
    for (const [m, rows] of Object.entries(resupply))
      out.push({ Icon: Undo2, c: C.destructive, key: `x-${m}`,
                 title: `Re-supply: ${m}`,
                 detail: `Prior mapping rejected on ${rows.length} requirement${rows.length > 1 ? "s" : ""} — upload a corrected value.`,
                 leverage: rows.length, onClick: () => onFocusMetric(m) });
    for (const row of calcs)
      out.push({ Icon: Calculator, c: C.destructive, key: `c-${row.requirement_id}`,
                 title: `Run a calculation: ${row.framework}`,
                 detail: REASON_TEXT.wrong_granularity, leverage: 1 });
    if (awaiting)
      out.push({ Icon: UserCheck, c: C.locked, key: "r-all",
                 title: `Obtain approval: ${awaiting} element${awaiting > 1 ? "s" : ""} awaiting review`,
                 detail: "Reviewer acceptance records the evidence mapping into the audit trail.",
                 leverage: awaiting });
    return out.sort((a, b) => b.leverage - a.leverage);
  }, [coverage, onFocusMetric]);

  if (!actions.length) return null;
  return (
    <Card>
      <Overline Icon={RefreshCw} color={C.body}>What to provide next</Overline>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {actions.map((a) => (
          <button key={a.key} onClick={a.onClick} disabled={!a.onClick}
                  data-testid={`action-${a.key}`}
                  style={{ display: "flex", gap: 12, alignItems: "flex-start", textAlign: "left",
                           background: "transparent", border: `1px solid ${C.border}`,
                           borderRadius: 8, padding: "10px 14px",
                           cursor: a.onClick ? "pointer" : "default", width: "100%" }}>
            <a.Icon size={15} strokeWidth={1.5} color={a.c} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              <span style={{ display: "block", fontFamily: SANS, fontSize: 13,
                             fontWeight: 600, color: C.ink }}>{a.title}</span>
              <span style={{ display: "block", fontFamily: SANS, fontSize: 12,
                             color: C.body, marginTop: 2, maxWidth: "68ch" }}>{a.detail}</span>
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

// ─── The data-bearing view ────────────────────────────────────────────────────
export default function CoverageView({ entityId, cycleId, boundary, highlight, onHighlight }) {
  const [coverage, setCoverage] = useState(null);
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    setCoverage(null);
    apiFetch(`/api/v1/spine/coverage?entity_id=${encodeURIComponent(entityId)}&cycle_id=${encodeURIComponent(cycleId)}`)
      .then((body) => {
        setCoverage(body.coverage || []);
        setVersions(body.versions || null);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, [entityId, cycleId]);
  useEffect(load, [load]);

  // `busy` is deliberately global: one review mutation at a time keeps
  // write-ordering trivially safe; the endpoints are fast.
  const act = useCallback(async (path, body) => {
    setBusy(true);
    try {
      await apiFetch(path, { method: "POST", body });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [load]);

  const onConfirm = useCallback((el, verb) =>
    act(`/api/v1/spine/disclosures/${el.disclosure_id}/${verb}`,
        verb === "confirm" ? {} : undefined), [act]);
  const onReject = useCallback((el, reason_code) =>
    act(`/api/v1/spine/disclosures/${el.disclosure_id}/reject`, { reason_code }), [act]);

  const putElement = useCallback(async (el, sub, body, failWord) => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token") || "";
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/spine/elements/${encodeURIComponent(el.element_id)}/${sub}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json",
                     ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ entity_id: entityId, cycle_id: cycleId, ...body }),
        });
      if (!r.ok) {
        let detail = failWord;
        try { detail = (await r.json())?.detail || detail; } catch { /* noop */ }
        throw new Error(typeof detail === "string" ? detail : failWord);
      }
      load();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }, [entityId, cycleId, load]);

  const onOmit = useCallback((el, value, statement) =>
    putElement(el, "applicability", { value, statement }, "omission failed"),
    [putElement]);
  const onAttest = useCallback((el, value, comment) =>
    putElement(el, "review", { value, comment }, "attestation failed"),
    [putElement]);

  const sums = useMemo(() => (coverage ? summarize(coverage) : null), [coverage]);
  const metrics = useMemo(() => {
    if (!coverage) return [];
    const s = new Set();
    for (const row of coverage)
      for (const el of row.elements || [])
        if (el.metric_code && el.disclosure_id) s.add(el.metric_code);
    return [...s].sort();
  }, [coverage]);
  const highlightSpread = useMemo(() => {
    if (!highlight || !coverage) return null;
    let els = 0; const fws = new Set();
    for (const row of coverage)
      for (const el of row.elements || [])
        if (el.metric_code === highlight && el.disclosure_id) {
          els += 1; fws.add(row.framework);
        }
    return { elements: els, frameworks: fws.size };
  }, [highlight, coverage]);

  const loading = !coverage && !error;

  return (
    <>
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {/* Summary — renders ONLY after a successful coverage response */}
      {sums && (
        <div style={{ textAlign: "right", marginBottom: 16 }}
             data-testid="coverage-summary" aria-live="polite">
          <div>
            <Data size={16}>{sums.supported}</Data>
            <span style={{ fontFamily: SANS, fontSize: 12, color: C.body }}> of </span>
            <Data size={16}>{sums.total}</Data>
            <span style={{ fontFamily: SANS, fontSize: 12, color: C.body }}>
              {" "}applicable evidence elements supported
            </span>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 11, color: C.muted, marginTop: 2 }}>
            {sums.confirmed} confirmed · {sums.awaiting} awaiting review
            {sums.rejected > 0 && ` · ${sums.rejected} rejected`} · {sums.missing} missing
            {sums.omitted > 0 && ` · ${sums.omitted} omitted`}
          </div>
        </div>
      )}

      {/* Metric highlight chips */}
      {metrics.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
                      marginBottom: 18 }}>
          <span style={{ fontFamily: SANS, fontSize: 12, color: C.muted }}>
            Trace a metric:
          </span>
          {metrics.map((m) => {
            const on = m === highlight;
            return (
              <button key={m} onClick={() => onHighlight(on ? "" : m)}
                      data-testid={`metric-chip-${m}`} aria-pressed={on}
                      style={{ fontFamily: MONO, fontSize: 11, fontWeight: 600,
                               padding: "4px 11px", borderRadius: 999, cursor: "pointer",
                               color: on ? C.bg : C.body,
                               background: on ? C.primary : "transparent",
                               border: `1px solid ${on ? C.primary : C.border}`,
                               transition: "background 150ms ease-out, color 150ms ease-out" }}>
                {short(m)}
              </button>
            );
          })}
          {highlightSpread && (
            <span style={{ fontFamily: SANS, fontSize: 12, color: C.ink, marginLeft: 4 }}
                  data-testid="highlight-spread" aria-live="polite">
              Supports <Data size={12}>{highlightSpread.elements}</Data> disclosure
              element{highlightSpread.elements !== 1 ? "s" : ""} across{" "}
              <Data size={12}>{highlightSpread.frameworks}</Data> framework
              {highlightSpread.frameworks !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      {/* Main grid */}
      <div style={{ display: "grid", gap: 18,
                    gridTemplateColumns: "minmax(0, 1fr) minmax(260px, 320px)" }}
           className="coverage-grid">
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 20 }} data-testid="coverage-loading"><Skeleton h={280} /></div>
          ) : !coverage?.length ? (
            <div style={{ padding: 20 }}>
              <EmptyState Icon={CircleDashed} title="No requirements in view"
                          body="This cycle has no coverage rows yet for this entity. Upload a GHG datapoint and requirements will light up here." />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
                <thead>
                  <tr>
                    <th style={th} aria-label="Expand" />
                    <th style={th}>Requirement · standard</th>
                    <th style={th}>Framework</th>
                    <th style={th}>Status</th>
                    <th style={th}>Elements</th>
                    <th style={{ ...th, textAlign: "right" }}>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {coverage.map((row) => (
                    <RequirementRow key={row.requirement_id} row={row}
                                    standard={versions?.standards?.[row.requirement_id] || ""}
                                    highlight={highlight}
                                    expanded={!!expanded[row.requirement_id]}
                                    onToggle={() => setExpanded((x) => ({
                                      ...x, [row.requirement_id]: !x[row.requirement_id] }))}
                                    onConfirm={onConfirm} onReject={onReject}
                                    onOmit={onOmit} onAttest={onAttest} busy={busy} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {coverage && <NextActions coverage={coverage}
                                    onFocusMetric={(m) => onHighlight(m)} />}
          <Card>
            <Overline color={C.body}>Legend</Overline>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Badge c={C.primary} Icon={Shield} label="Defended" />
              {Object.values(STATE).map((s) => (
                <Badge key={s.label} c={s.c} Icon={s.Icon} label={s.label} />
              ))}
              <Badge c={C.muted} Icon={EyeOff} label="Not material / not applicable" />
              <p style={{ fontFamily: SANS, fontSize: 11, color: C.muted,
                          margin: "6px 0 0", maxWidth: "40ch" }}>
                Defended is computed, never asserted: every applicable element
                complete, current and reviewer-confirmed. Omissions carry an
                explicit recorded statement.
              </p>
            </div>
          </Card>
        </div>
      </div>

      {versions && (
        <footer style={{ marginTop: 18, fontFamily: MONO, fontSize: 10,
                         color: C.muted }} data-testid="versions-footer">
          mapping engine v{versions.engine} · evaluated {versions.evaluated_at}
        </footer>
      )}

      <style>{`
        @media (max-width: 900px) {
          .coverage-grid { grid-template-columns: minmax(0, 1fr) !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .coverage-grid *, [data-testid^="req-"], [data-testid^="element-"] {
            transition: none !important;
          }
        }
      `}</style>
    </>
  );
}
